import "./StockChartComponent.scss";
import {IComponentOptions, IHttpService, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {ChartData, ChartSettings, OHLC, StockChartData, Volatility} from "./models/StockChartData";
import {ChartIQDataPoint, ChartIQSeries} from "../../../components/chartiq/types/ChartIQFeed";
import moment from "moment";
import {Optional} from "../../../js/util/Optional";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {BreakpointObserver} from "../../../services/breakpoints/BreakpointObserver";
import {Breakpoint} from "../../../services/breakpoints/Breakpoint";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {SystemEvent} from "../../../services/system-state/SystemEvent";

export const StockChart = "tfStrategyBuilderStockChart";

export function stockChart(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/stock-chart/StockChartComponent.html`,
        bindings: {
            code: "<"
        },
        controllerAs: "ctrl",
        controller: StockChartComponentController
    };
}

declare const Papa: any;

const DesktopChartHeight = 290;
const MobileChartHeight = 240;

export class StockChartComponentController implements IOnInit, IOnDestroy {

    /* Input Binding */
    public set code(code: string | null) {
        if (code != null) {
            this.code$.next(code);
        }
        this._code = code;
    }

    public get code(): string | null {
        return this._code;
    }

    public chartSettings: ChartSettings;

    public optionsPrice: any;
    public optionsPricePoints: any;
    public optionsVolatility: any;

    public priceDates: Array<Date> | null;
    public priceData: ChartData<OHLC> | null;
    public volDates: Array<Date> | null;
    public volData: ChartData<Volatility> | null;

    private _code: string | null;

    private priceChartApi: NVD3ChartApi | null;
    private volChartApi: NVD3ChartApi | null;

    private readonly chartSetting$ = new ReplaySubject<ChartSettings>(1);
    private readonly code$ = new ReplaySubject<string>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $http: IHttpService,
                       private readonly $timeout: ITimeoutService,
                       private readonly breakpointObserver: BreakpointObserver,
                       private readonly systemEventService: SystemEventService) {
    }

    public $onInit(): void {
        const source$ = Observable.combineLatest(this.code$, this.chartSetting$);
        source$.takeUntil(this.destroyed$)
               .switchMap(([code, settings]) => this.loadChartData(code, settings))
               .map((data) => this.updateChartData(data))
               .subscribe(() => this.refresh());

        this.chartSetting$
            .takeUntil(this.destroyed$)
            .subscribe((settings) => {
                this.$timeout(() => this.chartSettings = settings);
            });

        const initialSettings: ChartSettings = {
            type: "optionsPrice",
            range: "1y"
        };

        this.chartSetting$.next(initialSettings);

        this.breakpointObserver
            .watch(Breakpoint.XS)
            .takeUntil(this.destroyed$)
            .map((change) => change.active)
            .map((active) => active ? MobileChartHeight : DesktopChartHeight)
            .subscribe((height) => this.configureCharts(height));

        this.systemEventService
            .eventsOfType(SystemEvent.VIEW_RESIZE)
            .takeUntil(this.destroyed$)
            .subscribe(() => this.refresh());
    }

    private async loadChartData(code: string, settings: ChartSettings): Promise<StockChartData> {
        const range = settings.range;
        const url = `/tradefloor/api/prices/chart/${code}?r=${range}`;
        const response = await this.$http.get<string>(url);

        const data = Optional.of(response)
                             .map((resp) => resp.data)
                             .map((raw) => this.parseCsv(raw))
                             .map((raw) => this.toSeries(raw))
                             .orElse([]);

        const priceDates: Array<Date> = [];
        const priceData: Array<OHLC> = [];
        const volData: Array<Volatility> = [];
        const volDates: Array<Date> = [];

        data.forEach((row) => {
            priceDates.push(row.DT!);
            priceData.push(this.priceDataRow(row));
            volData.push(this.volDataRow(row));
            volDates.push(row.DT!);
        });

        return {
            priceDates,
            priceData,
            volData,
            volDates
        };
    }

    private parseCsv(raw: string): Array<Array<string>> {
        const rows = Papa.parse(raw).data;
        rows.shift(); // remove the header row

        const rowHasData = (row: Array<string>): boolean => {
            return row != null && row.length > 0 && row[0] != null && row[0] !== "";
        };

        return rows.filter(rowHasData);
    }

    private toSeries(data: Array<Array<string>>): ChartIQSeries {
        return data.map(entry => {
            const date = moment(entry[0]).toDate();
            const iv30 = parseFloat(entry[5]);
            const ivRank = parseFloat(entry[6]);
            const volume = parseFloat(entry[7]);

            return {
                DT: date,
                Open: parseFloat(entry[1]),
                High: parseFloat(entry[2]),
                Low: parseFloat(entry[3]),
                Close: parseFloat(entry[4]),
                IV30: iv30 * 100,
                IvRank: ivRank,
                Volume: volume
            };
        });
    }

    private priceDataRow(row: ChartIQDataPoint): OHLC {
        return {
            date: row.DT!,
            open: row.Open,
            high: row.High,
            low: row.Low,
            close: row.Close
        };
    }

    private volDataRow(row: ChartIQDataPoint): Volatility {
        return {
            date: row.DT!,
            IV30: row.IV30
        };
    }

    private updateChartData(data: StockChartData) {
        this.$timeout(() => {
            this.priceDates = data.priceDates;
            this.priceData = [{values: data.priceData}];
            this.volDates = data.volDates;
            this.volData = [{values: data.volData}];
        });
    }

    public settingsChanged() {
        const settings = ObjectUtil.copy(this.chartSettings);
        this.chartSetting$.next(settings);
    }

    private priceChartReady($scope: any) {
        this.priceChartApi = $scope.api;
    }

    private volChartReady($scope: any) {
        this.volChartApi = $scope.api;
    }

    private refresh() {
        this.$timeout(() => {
            if (this.priceChartApi != null) {
                this.priceChartApi.refresh();
            }
            if (this.volChartApi != null) {
                this.volChartApi.refresh();
            }
        });
    }

    private configureCharts(chartHeight: number) {

        this.optionsPrice = {
            chart: {
                type: "candlestickBarChart",
                height: chartHeight,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 60
                },
                x(d: any) {
                    return d["date"];
                },
                y(d: any) {
                    return d["close"];
                },
                duration: 100,

                xAxis: {
                    axisLabel: "Dates",
                    tickFormat: (d: any) => {
                        switch (this.chartSettings.range) {
                            case "30d":
                            case "90d":
                            case "1y":
                            case "5y":
                                return d3.time.format("%d/%m/%y")(new Date(d));
                            default:
                                return d3.time.format("%d/%m/%y %H:%M")(new Date(d));
                        }
                    },
                    showMaxMin: false
                },

                yAxis: {
                    axisLabel: "Stock Price",
                    tickFormat(d: any) {
                        return "$" + d3.format(",.3f")(d);
                    },
                    showMaxMin: false
                },
                zoom: {
                    enabled: true,
                    scaleExtent: [1, 10],
                    useFixedDomain: false,
                    useNiceScale: false,
                    horizontalOff: false,
                    verticalOff: true,
                    unzoomEventType: "dblclick.zoom"
                }
            }
        };

        this.optionsPricePoints = {
            chart: {
                type: "candlestickBarChart",
                height: chartHeight,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 60
                },
                x(d: any) {
                    return d["date"];
                },
                y(d: any) {
                    return d["close"];
                },
                duration: 100,

                xAxis: {
                    axisLabel: "Dates",
                    tickFormat: (d: any) => {
                        switch (this.chartSettings.range) {
                            case "30d":
                            case "90d":
                            case "1y":
                            case "5y":
                                return d3.time.format("%d/%m/%y")(new Date(d));
                            default:
                                return d3.time.format("%d/%m/%y %H:%M")(new Date(d));
                        }
                    },
                    showMaxMin: false
                },

                yAxis: {
                    axisLabel: "Index",
                    tickFormat(d: any) {
                        return d3.format(",.1f")(d);
                    },
                    showMaxMin: false
                },
                zoom: {
                    enabled: true,
                    scaleExtent: [1, 10],
                    useFixedDomain: false,
                    useNiceScale: false,
                    horizontalOff: false,
                    verticalOff: true,
                    unzoomEventType: "dblclick.zoom"
                }
            }
        };

        this.optionsVolatility = {
            chart: {
                type: "lineChart",
                height: chartHeight,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 60
                },
                x(d: any) {
                    return d["date"];
                },
                y(d: any) {
                    return d["IV30"];
                },
                duration: 100,

                showLegend: false,

                xAxis: {
                    axisLabel: "Dates",
                    tickFormat: (d: any) => {
                        return d3.time.format("%d/%m/%y")(new Date(d));
                    },
                    showMaxMin: false
                },

                yAxis: {
                    axisLabel: "IV 30 days (%)",
                    tickFormat(d: any) {
                        return d3.format(",.2f")(d);
                    },
                    showMaxMin: false
                },
                zoom: {
                    enabled: true,
                    scaleExtent: [1, 10],
                    useFixedDomain: false,
                    useNiceScale: false,
                    horizontalOff: false,
                    verticalOff: true,
                    unzoomEventType: "dblclick.zoom"
                }
            }
        };
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
